import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@lib/firebase";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { serverTimestamp } from "firebase/firestore";
import { InputFieldVolunteer } from "../common/InputFieldVolunteer";

export const FormPendaftaranVolunteer = ({ labelColor = "text-slate-800" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    namaLengkap: "",
    telepon: "",
    nomorIdentitas: "",
    linkSertifikat: "",
    usernameInstagram: "",
    tanggalLahir: "",
    gender: "",
    negara: "Indonesia",
    provinsi: "",
    kota: "",
    alamat: "",
    motivasiDanPengalaman: "",
    ukuranJersey: "",
    posisiVolunteer: "",
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState(null);

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Identity Number";
    if (str === "motivasiDanPengalaman")
      return "Volunteer Motivation and Experience";
    if (str === "ukuranJersey") return "Jersey Size";
    if (str === "linkSertifikat") return "Certificate Link";
    if (str === "usernameInstagram") return "Instagram Username";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const checkRegistration = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, "pendaftaranVolunteers"),
        where("uid", "==", user.uid),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setRegistrationStatus("registered");
      } else {
        setRegistrationStatus("not_registered");
      }
    } catch (err) {
      setErrorMsg("Failed to check registration status. Please try again.");
      showNotification("error", "Failed to check registration status.");
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/Login");
      showNotification(
        "error",
        "Please login to register as a volunteer."
      );
      return;
    }

    const initialize = async () => {
      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        let userData = {};

        if (docSnap.exists()) {
          userData = docSnap.data();
          let namaDepan = userData.namaDepan || "";
          let namaBelakang = userData.namaBelakang || "";
          let namaLengkap = userData.namaLengkap || "";

          if (!namaDepan && !namaBelakang && namaLengkap) {
            const namaSplit = namaLengkap.trim().split(/\s+/);
            namaDepan = namaSplit[0] || "";
            namaBelakang = namaSplit.slice(1).join(" ") || "";
          }

          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap: namaLengkap || `${namaDepan} ${namaBelakang}`.trim(),
            telepon: userData.telepon || "",
            nomorIdentitas: userData.nomorIdentitas || "",
            linkSertifikat: userData.linkSertifikat || "",
            usernameInstagram: userData.usernameInstagram || "",
            tanggalLahir: userData.tanggalLahir || "",
            gender: userData.gender || "",
            negara: userData.negara || "Indonesia",
            provinsi: userData.provinsi || "",
            kota: userData.kota || "",
            alamat: userData.alamat || "",
            motivasiDanPengalaman: userData.motivasiDanPengalaman || "",
            ukuranJersey: userData.ukuranJersey || "",
          }));
        } else {
          const namaSplit = (user.displayName || "").trim().split(/\s+/);
          const namaDepan = namaSplit[0] || "";
          const namaBelakang = namaSplit.slice(1).join(" ") || "";
          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap:
              namaDepan && namaBelakang
                ? `${namaDepan} ${namaBelakang}`.trim()
                : "",
            linkSertifikat: "",
            usernameInstagram: "",
          }));
        }

        await checkRegistration();
      } catch (err) {
        setErrorMsg("Failed to fetch user data.");
        showNotification("error", "Failed to fetch user data.");
      }
    };

    initialize();
  }, [navigate, showNotification, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "namaDepan" || name === "namaBelakang") {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        updated.namaLengkap = `${updated.namaDepan || ""} ${
          updated.namaBelakang || ""
        }`.trim();
        return updated;
      });
    } else if (name === "namaLengkap") {
      const namaSplit = value.trim().split(/\s+/);
      setFormData((prev) => ({
        ...prev,
        namaLengkap: value,
        namaDepan: namaSplit[0] || "",
        namaBelakang: namaSplit.slice(1).join(" ") || "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateFormat = (field, value, negara = "Indonesia") => {
    const label = humanize(field);

    if (value === undefined || value === null || value === "") {
      if (field === "linkSertifikat" || field === "usernameInstagram") {
        return "";
      }
      return `${label} is required`;
    }
    if (
      (field === "namaDepan" || field === "namaBelakang") &&
      !/^[A-Za-z\s]{1,50}$/.test(value)
    ) {
      return `${label} must be letters and spaces only, maximum 50 characters.`;
    }
    if (field === "telepon" && !/^\+?\d{8,15}$/.test(value)) {
      return `${label} must be 8-15 digits, can start with "+".`;
    }
    if (field === "nomorIdentitas") {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (ID Card) must be 16 digits for Indonesian participants.`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Passport/ID) must be 5-20 alphanumeric characters for foreign participants.`;
        }
      }
    }
    if (
      field === "linkSertifikat" &&
      value &&
      !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(value)
    ) {
      return `${label} must be a valid URL, e.g., "https://yourcertificatelink"`;
    }
    if (
      field === "usernameInstagram" &&
      value &&
      !/^[A-Za-z0-9._]{1,30}$/.test(value)
    ) {
      return `${label} must be letters, numbers, dots, or underscores, maximum 30 characters, e.g., "user_name"`;
    }
    if (field === "tanggalLahir") {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 18,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} must be in the past before 27/9/2025, e.g., "2005-09-27"`;
      } else if (date > minAgeDate) {
        return `Volunteer must be at least 18 years old on 27/9/2025, e.g., "2007-09-27" or earlier`;
      }
    }
    if (field === "gender" && !["Male", "Female"].includes(value)) {
      return `${label} must be Male or Female.`;
    }
    if (field === "alamat" && !/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
      return `${label} must be 5-100 characters, can be letters, numbers, spaces, commas, dots, hyphens, slashes, hashes, or parentheses.`;
    }
    if (
      field === "motivasiDanPengalaman" &&
      !/^[A-Za-z0-9\s,.!?-]{10,500}$/.test(value)
    ) {
      return `${label} must be 10-500 characters, can be letters, numbers, spaces, and certain punctuation.`;
    }
    if (
      field === "ukuranJersey" &&
      !["XS", "S", "M", "L", "XL", "XXL"].includes(value)
    ) {
      return `${label} must be selected from the available sizes.`;
    }
    if (
      field === "posisiVolunteer" &&
      ![
        "Marshalls",
        "Water Station Crew",
        "Racepack Collection Crew",
        "Race Venue Crew",
        "Medis & Rescue",
        "Photographer",
        "Check Point",
        "Logistik",
      ].includes(value)
    ) {
      return `${label} must be selected from the available positions.`;
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "motivasiDanPengalaman",
      "ukuranJersey",
      "posisiVolunteer",
    ];

    const errors = {};
    for (const field of requiredFields) {
      const error = validateFormat(field, formData[field], formData.negara);
      if (error) {
        errors[field] = error;
      }
    }

    if (formData.linkSertifikat) {
      const error = validateFormat("linkSertifikat", formData.linkSertifikat);
      if (error) errors.linkSertifikat = error;
    }
    if (formData.usernameInstagram) {
      const error = validateFormat(
        "usernameInstagram",
        formData.usernameInstagram
      );
      if (error) errors.usernameInstagram = error;
    }

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      setErrorMsg(firstError);
      showNotification("error", firstError);
      setLoading(false);
      return;
    }

    try {
      const sanitizedFormData = {
        namaDepan: formData.namaDepan?.trim().replace(/\s+/g, " "),
        namaBelakang: formData.namaBelakang?.trim().replace(/\s+/g, " "),
        namaLengkap: formData.namaLengkap?.trim().replace(/\s+/g, " "),
        telepon: formData.telepon?.trim().replace(/\s+/g, ""),
        nomorIdentitas: formData.nomorIdentitas?.trim().replace(/\s+/g, ""),
        linkSertifikat: formData.linkSertifikat?.trim() || "",
        usernameInstagram: formData.usernameInstagram?.trim() || "",
        tanggalLahir: formData.tanggalLahir?.trim(),
        gender: formData.gender?.trim(),
        negara: formData.negara?.trim(),
        provinsi: formData.provinsi?.trim(),
        kota: formData.kota?.trim(),
        alamat: formData.alamat?.trim().replace(/\s+/g, " "),
        motivasiDanPengalaman: formData.motivasiDanPengalaman
          ?.trim()
          .replace(/\s+/g, " "),
        ukuranJersey: formData.ukuranJersey?.trim(),
        posisiVolunteer: formData.posisiVolunteer?.trim(),
      };

      const payload = {
        ...sanitizedFormData,
        uid: user.uid,
        createdAt: serverTimestamp(),
        status: null,
      };

      await addDoc(collection(db, "pendaftaranVolunteers"), payload);
      setSuccessMsg("Volunteer registration successful! Waiting for committee review.");
      setRegistrationStatus("registered");
      showNotification(
        "success",
        "Volunteer registration successful! Waiting for committee review."
      );
      setTimeout(() => navigate("/MyRegistration"), 2000);
    } catch (error) {
      const errorMessage =
        error.message || "Volunteer registration failed. Please try again.";
      setErrorMsg(errorMessage);
      showNotification("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white shadow-xl px-4 sm:px-6 lg:px-8 z-0">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4 py-20 overflow-visible">
        <div className="w-full md:w-3/4 max-w-4xl bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md">
          <h2 className="text-2xl font-semibold text-left mb-4">
            JOTR 2025 Volunteer Registration Form
          </h2>
          <form id="form-pendaftaran-volunteer" onSubmit={handleSubmit}>
            <InputFieldVolunteer
              formData={formData}
              handleChange={handleChange}
              isFormDisabled={registrationStatus === "registered" || loading}
              labelColor={labelColor}
            />
          </form>
        </div>

        <div className="w-full md:w-1/4 max-w-sm bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-left">
            Registration Information
          </h2>
          <div className="text-lg text-left">
            <p>
              Please complete your data. The committee will contact you if you pass
              the selection. Thank you for your participation!
            </p>
          </div>

          {(errorMsg || successMsg) && (
            <p
              className={`text-left font-medium ${
                errorMsg ? "text-rose-600" : "text-slate-800"
              }`}
            >
              {errorMsg || successMsg}
            </p>
          )}

          <div>
            {registrationStatus === "registered" ? (
              <button
                type="button"
                className="w-full py-3 bg-slate-400 font-semibold rounded-xl cursor-not-allowed opacity-50"
                disabled
              >
                Already Registered
              </button>
            ) : (
              <button
                type="submit"
                form="form-pendaftaran-volunteer"
                className={`w-full py-3 bg-lime-600 hover:bg-lime-700 transition-all duration-300 text-white font-semibold rounded-xl ${
                  loading ? "cursor-not-allowed opacity-50" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Loading..." : "Register Volunteer"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
